import { IconButton, Stack } from '@mui/material';
import {
  Assessment,
  AssessmentsGrade,
  FilterKeys,
  GroupWithMembers,
  useGetAssessmentsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useGridApiProvider } from '@schooly/components/data-grid';
import { useNotifications } from '@schooly/components/notifications';
import { AccessDenied, CrossIcon, Loading, ModalHeader, ModalLarge } from '@schooly/style';
import { useSelectIdsContext } from '@schooly/utils/bulk-actions';
import { FC, useCallback, useMemo, useState } from 'react';

import { useGroup } from '../../../context/groups/useGroup';
import { useRouter } from '../../../context/router/useRouter';
import { MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH } from '../Markbook/common/assessmentStyling';
import {
  AverageResults,
  AverageResultsColumnData,
  AverageResultsWrapper,
} from '../Markbook/common/AverageResults';
import {
  createGradeFinder,
  getAssessmentEntryColorFactory,
  getAverageColorFactory,
} from '../Markbook/common/colouredCells';
import { MarkbookForGroupsStudentsHeaderCellProps } from '../Markbook/common/MarkbookCell';
import { MarkbookBaseline, MarkbookCategoryWithStudents } from '../Markbook/common/types';
import { useBaselineAssessmentConfig } from '../Markbook/common/useBaselineAssessmentsConfig';
import { getBaseline, getMarkbookSubject, MARKBOOK_TUTOR_GROUP } from '../Markbook/common/utils';
import { MarkbookForGroups } from '../Markbook/MarkbookForGroups';
import { DropdownMarkbookSettings } from '../Markbook/MarkbookSettings/DropdownMarkbookSettings';
import {
  DEFAULT_MARKBOOK_VIEW_SETTINGS,
  MarkbookOverrideValueAndDisable,
  MarkbookViewSettings,
} from '../Markbook/MarkbookSettings/MarkbookSettings';
import { shouldDisableScoreInPercentage } from '../Markbook/MarkbookSettings/utils';
import { AssessmentMarkbookExport } from './AssessmentMarkbookExport';
import { DEFAULT_ASSESSMENTS_SORT, useAssessmentsMarkbook } from './useAssessmentsMarkbook';

export const AssessmentMarkbookModal: FC = () => {
  const { schoolId = '' } = useAuth();
  const { goBack } = useRouter();
  const { group, error, groupId = '' } = useGroup();
  const { selectedIds } = useSelectIdsContext();
  const { data: assessmentsData, error: assessmentsError } = useGetAssessmentsQuery(
    {
      school_id: schoolId,
      sort: DEFAULT_ASSESSMENTS_SORT,
      filters: {
        [FilterKeys.Group]: [groupId],
        [FilterKeys.Assessment]:
          selectedIds !== 'all' && selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
      },
    },
    {
      enabled: !!groupId,
      refetchOnMount: 'always',
    },
  );

  const { assessments } = useMemo(() => {
    const assessments =
      assessmentsData?.pages.reduce<Assessment[]>(
        (prev, curr) => [
          ...prev,
          ...curr.results.map((a) => ({
            ...a,
            methods: a.methods,
          })),
        ],
        [],
      ) ?? [];

    return { assessments };
  }, [assessmentsData]);

  const renderContent = () => {
    if (error || assessmentsError) return <AccessDenied />;
    if (!group || !assessments.length) return <Loading />;
    return (
      <AssessmentMarkbookModalContent group={group} onGoBack={goBack} assessments={assessments} />
    );
  };

  return (
    <ModalLarge onClose={goBack} open sx={{ '.MuiPaper-root': { mb: 0 } }}>
      {renderContent()}
    </ModalLarge>
  );
};

const noop = () => {};

const AssessmentMarkbookModalContent: FC<{
  group: GroupWithMembers;
  assessments: Assessment[];
  onGoBack: () => void;
}> = ({ group, onGoBack, assessments }) => {
  const { showError } = useNotifications();
  const [settings, setSettings] = useState<MarkbookViewSettings>(DEFAULT_MARKBOOK_VIEW_SETTINGS);

  const {
    dateFrom,
    dateTo,
    markbookData,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    averageData,
    subjectAverageData,
    selectedIds,
    benchmarkEnabled,
    fetchingLists,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    setBenchmarkEnabled,
    selectLists,
    isFetchingNextPage,
    isAverageLoading,
    refetch,
    handleSuccessfulEntryUpdate,
  } = useAssessmentsMarkbook({
    group,
    settings,
    assessments,
  });

  const { baselineAssessmentConfig, setBaselineAssessmentConfig } = useBaselineAssessmentConfig({
    settings,
    setBenchmarkEnabled,
    benchmarkEnabled,
  });

  const overrideSettings: MarkbookOverrideValueAndDisable = useMemo(() => {
    return {
      scoreInPercentage: shouldDisableScoreInPercentage(assessments) ? false : undefined,
      coloredCells: baselineAssessmentConfig ? true : undefined,
    };
  }, [assessments, baselineAssessmentConfig]);

  const { onApiRefReady, renderWithProvider } = useGridApiProvider();

  const avgResultEnabled = overrideSettings.avgResults ?? settings.avgResults;
  const coloredCellsEnabled = overrideSettings.coloredCells ?? settings.coloredCells;
  const scoreInPercentageEnabled = overrideSettings.scoreInPercentage ?? settings.scoreInPercentage;

  const categories: MarkbookCategoryWithStudents[] = useMemo(() => {
    const { pages } = markbookData ?? {};

    // biome-ignore lint/complexity/noFlatMapIdentity: Legacy code
    return (pages?.flatMap((p) => p) ?? []).map((p) => ({
      category: p.category,
      students: p.students.results,
      totalStudents: p.students.count,
    }));
  }, [markbookData]);

  const { studentCount, assessmentsWithFilteredGroups, baseline } = useMemo(() => {
    const studentCount = categories.reduce((acc, c) => acc + c.totalStudents, 0);

    const assessmentsWithFilteredGroups = assessments.map((a) => {
      return { ...a, groups: [group] };
    });

    const baseline: MarkbookBaseline | undefined = getBaseline(
      assessmentsWithFilteredGroups,
      baselineAssessmentConfig,
    );
    return { studentCount, assessmentsWithFilteredGroups, baseline };
  }, [assessments, baselineAssessmentConfig, categories, group]);

  const lists = useMemo(
    () =>
      Object.values(selectLists ?? {}).reduce<Record<string, AssessmentsGrade>>(
        (acc, list) => ({ ...acc, [list.id]: list }),
        {},
      ),
    [selectLists],
  );

  const gradeFinders = useMemo(() => {
    const finders = new Map<string, ReturnType<typeof createGradeFinder>>();

    if (!avgResultEnabled && !coloredCellsEnabled) return finders;

    Object.entries(lists ?? {}).forEach(([selectListId, grade]) => {
      finders.set(selectListId, createGradeFinder(grade.options));
    });

    return finders;
  }, [lists, avgResultEnabled, coloredCellsEnabled]);

  const { averageResultsColumns, fixedColumn } = useMemo(() => {
    if (!avgResultEnabled) return { averageResultsColumns: [] };
    let fixedColumn: AverageResultsColumnData | undefined;
    const baseline = getBaseline(assessments, baselineAssessmentConfig);

    switch (baseline?.kind) {
      case 'assessment':
        fixedColumn = {
          type: 'assessment' as const,
          assessment: baseline.assessment,
          subject: baseline.subject,
          methodId: baseline.method.id ?? '',
        };
        break;
      case 'average': {
        const subjectId =
          baseline.subject.id === MARKBOOK_TUTOR_GROUP.id ? null : baseline.subject.id;
        const average = subjectAverageData.find((a) => a.subject_id === subjectId);

        fixedColumn = {
          type: 'average' as const,
          subject: baseline.subject,
          average: average?.average_score ?? null,
        };
        break;
      }
    }

    const groupSubject = getMarkbookSubject(group);

    return {
      averageResultsColumns: [
        ...assessments.flatMap((assessment) =>
          assessment.methods.map((method) => ({
            type: 'assessment' as const,
            assessment,
            subject: baseline?.subject ?? undefined,
            methodId: method.id ?? '',
          })),
        ),
        {
          type: 'average' as const,
          subject: groupSubject,
          average: subjectAverageData[0]?.average_score ?? null,
        },
      ],
      fixedColumn,
    };
  }, [avgResultEnabled, assessments, baselineAssessmentConfig, subjectAverageData, group]);

  const studentsHeaderCellProps: MarkbookForGroupsStudentsHeaderCellProps = useMemo(() => {
    return { kind: 'students', count: studentCount, textId: 'userType-student-plural' };
  }, [studentCount]);

  const isFetchingNextPageCallback = useCallback(() => {
    return isFetchingNextPage;
  }, [isFetchingNextPage]);

  const hasNextPageCallback = useCallback(() => {
    return !!hasNextPage;
  }, [hasNextPage]);

  const onFetchNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const onRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const getAssessmentEntryColor = coloredCellsEnabled
    ? getAssessmentEntryColorFactory(averageData, lists, gradeFinders)
    : undefined;

  const getAverageCellColor =
    coloredCellsEnabled && !baselineAssessmentConfig
      ? getAverageColorFactory(subjectAverageData)
      : undefined;

  const renderContent = () => {
    if (error && !isLoading) return <AccessDenied />;
    if (isLoading) return <Loading />;
    return (
      <>
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            px: 3,
            '.gridContainer': {
              border: `0 !important`,
            },
            '.categoryNameCell .MuiTypography-root': {
              display: 'none',
            },
          }}
        >
          <MarkbookForGroups
            studentsHeaderCellProps={studentsHeaderCellProps}
            baseline={baseline}
            onSetBaselineConfig={setBaselineAssessmentConfig}
            assessments={assessmentsWithFilteredGroups}
            categories={categories}
            hasNextPage={hasNextPageCallback}
            fetchNextPage={onFetchNextPage}
            isFetchingNextPage={isFetchingNextPageCallback}
            getAssessmentEntryColor={getAssessmentEntryColor}
            getAverageCellColor={getAverageCellColor}
            showStudentsAverage={avgResultEnabled}
            lists={lists}
            refetch={onRefetch}
            onChangeSort={noop}
            isSorting={false}
            columnArrangeBy={FilterKeys.Subject}
            showPercentage={settings.scoreInPercentage}
            onSuccessfulEntryUpdate={handleSuccessfulEntryUpdate}
            onApiRefReady={onApiRefReady}
            onUpdateError={showError}
          />
        </Stack>
        {renderWithProvider(
          <AverageResultsWrapper modalView opened={avgResultEnabled} sx={{ mx: 0 }}>
            <AverageResults
              columns={averageResultsColumns}
              fixedColumn={fixedColumn}
              averageData={averageData}
              subjectAverageData={subjectAverageData}
              setBenchmarkSubjectAverage={setBenchmarkSubjectAverage}
              isLoading={isAverageLoading || fetchingLists}
              studentColumnWidth={MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH}
              gradeFinders={gradeFinders}
              setBenchmarkEnabled={setBenchmarkEnabled}
              setBenchmarkAverage={setBenchmarkAverage}
              benchmarkEnabled={benchmarkEnabled}
              gradeList={lists}
              anchorOffset={6}
              showPercentage={scoreInPercentageEnabled}
            />
          </AverageResultsWrapper>,
        )}
      </>
    );
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0, height: '100%' }}>
      <ModalHeader title={group.name} active>
        <DropdownMarkbookSettings
          settings={settings}
          onSetSettings={setSettings}
          overrideValueAndDisable={overrideSettings}
        />
        <AssessmentMarkbookExport
          group={group}
          selectedIds={selectedIds}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
        <IconButton onClick={onGoBack}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      {renderContent()}
    </Stack>
  );
};
